import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map, takeUntil } from 'rxjs/operators';

import { Observable } from 'rxjs';
import { GetEnumValuesDto, SmsCp, UseTypes } from 'src/app/core/api/models';
import { EnumsService } from 'src/app/core/api/services';
import { EnumToTextConvertorPipe } from 'src/app/core/pipes/enum-text.pipe';
import { SavedEnumsService } from 'src/app/core/services/saved-enums.service';
import { AppPrivateNumberSelectSearch } from 'src/app/core/types/app-private-number_select-search.type';
import { CustomResultOfIListOfUserPrivateNumbersForSendDto } from '../../../core/api/models/custom-result-of-i-list-of-user-private-numbers-for-send-dto';
import { PrivateNumbersService } from '../../../core/api/services/private-numbers.service';
import { SendTypes } from './../../../core/api/models/send-types';
import { BaseSelectSearch } from './base/base-select-search.component';
import { BaseSelectSearchModule } from './base/base-select.search.module';

@Component({
  selector: 'private-number-select-search',
  templateUrl: './base/base-select-search-template.html',
  imports: [BaseSelectSearchModule],
})
export class PrivateNumberSelectSearchComponent
  extends BaseSelectSearch
  implements OnInit
{
  @Input() sendType?: SendTypes;
  @Input() useType?: UseTypes;
  @Input() organizationId?: number;
  @Input() userId?: number;
  @Input() smsCp?: SmsCp;
  @Input() isShowSmsCp?: boolean = false;
  @Input() type?: AppPrivateNumberSelectSearch = 'privateNumbersForSend';

  SmsCpTypesEnumValues: GetEnumValuesDto[] = [];

  constructor(
    protected currentService: PrivateNumbersService,
    private translate: TranslateService,
    private enumService: EnumsService,
    private enumToTextConvertorPipe: EnumToTextConvertorPipe
  ) {
    super();
  }

  ngOnInit() {
    this.getEnumValuesApi('SmsCp')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => {
        this.SmsCpTypesEnumValues = values;
        this.initConfig();
        this.getItems();
      });
  }

  initConfig() {
    if (!this.optionValue) this.optionValue = 'id';
    this.optionTitle = 'optionTitle';
    if (!this.label) this.label = 'common.privateNumber';
    if (!this.type) this.type = 'privateNumbersForSend';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] && !changes['organizationId']?.firstChange)
      this.getItems();
  }

  getItemsApi() {
    const voiceTitle = this.translate.instant('common.voiceMessage');
    switch (this.type) {
      case 'isSmartSendSms':
        const smartBody = {
          SmsCp: this.smsCp || null,
          SendType: this.sendType || null,
          OrganizationId: this.organizationId || null,
          UserId: this.userId || null,
        };
        this.currentService
          .privateNumbersGetUserPrivateNumbersForSend(smartBody)
          .pipe(
            this.basePipeOperators(),
            map((items: CustomResultOfIListOfUserPrivateNumbersForSendDto) => {
              //#region grouping
              const group = items?.data?.reduce((acc: any, curr) => {
                let key = curr?.number;
                if (!acc[key]) {
                  acc[key] = [];
                }
                acc[key].push(curr);
                return acc;
              }, {});
              let groupedItems = Object.keys(group).map((key) => ({
                number: key,
                items: group[key],
              }));
              //#endregion
              return groupedItems.map((item) => {
                //#region translate
                let translatedSmsCpType = '';
                if (this.isShowSmsCp) {
                  item.items.forEach((groupedItem) => {
                    if (item.number == groupedItem.number) {
                      translatedSmsCpType =
                        translatedSmsCpType +
                        ' ' +
                        this.enumToTextConvertorPipe.transform(
                          groupedItem.smsCp,
                          this.SmsCpTypesEnumValues
                        );
                    }
                  });
                  return {
                    ...item,
                    optionTitle: `${item.number} (${translatedSmsCpType}) ${
                      item.items[0].isCanSendVoice ? '(' + voiceTitle + ')' : ''
                    } `,
                  };
                }
                //#endregion
                else
                  return {
                    ...item,
                    optionTitle: `${item.number} ${
                      item.items[0].isCanSendVoice ? '(' + voiceTitle + ')' : ''
                    } `,
                  };
              });
            })
          )
          .subscribe(
            (items: any) => {
              if (items?.data) this.items = items?.data;
              else this.items = items;
              this.setDefaultValue();
              let number = this.items.find(
                (voiceMessage) => voiceMessage.number == this.value
              );
              if (number) {
                this.itemChange.emit(number);
              }
            },
            (error) => {
              this.emitError();
            },
            () => {
              this.initLoading = false;
              this.isFetchData = true;
            }
          );
        break;
      case 'privateNumbersForSend':
        const body = {
          SmsCp: this.smsCp || null,
          SendType: this.sendType || null,
          OrganizationId: this.organizationId || null,
          UserId: this.userId || null,
        };

        this.currentService
          .privateNumbersGetUserPrivateNumbersForSend(body)
          .pipe(
            this.basePipeOperators(),
            map((items: CustomResultOfIListOfUserPrivateNumbersForSendDto) => {
              return items?.data?.map((item) => {
                let translatedSmsCpType = '';
                if (this.isShowSmsCp) {
                  translatedSmsCpType = this.enumToTextConvertorPipe.transform(
                    item?.smsCp,
                    this.SmsCpTypesEnumValues
                  );
                  return {
                    ...item,
                    optionTitle: `${item?.number} (${translatedSmsCpType}) ${
                      item?.tag ? '[' + item?.tag + ']' : ''
                    } ${item?.isCanSendVoice ? '(' + voiceTitle + ')' : ''} `,
                  };
                } else
                  return {
                    ...item,
                    optionTitle: `${item?.number} ${
                      item?.tag ? '[' + item?.tag + ']' : ''
                    } ${item?.isCanSendVoice ? '(' + voiceTitle + ')' : ''} `,
                  };
              });
            })
          )
          .subscribe(this.baseObserver);
        break;
      case 'descendantPrivateNumbers':
        this.currentService
          .privateNumbersGetAllDescendantPrivateNumbers()
          .pipe(
            this.basePipeOperators(),
            map((items) => {
              return items.data.map((item) => {
                return {
                  ...item,
                  optionTitle: `${item.number} ${
                    item.isCanSendVoice ? '(' + voiceTitle + ')' : ''
                  } `,
                };
              });
            })
          )
          .subscribe(this.baseObserver);
        break;
    }
  }

  getEnumValuesApi(enumName): Observable<GetEnumValuesDto[]> {
    if (SavedEnumsService.getEnum(enumName) != null)
      return SavedEnumsService.getEnumAsObservable(enumName);

    this.enumService
      .enumsGetEnumValues({ EnumTypeName: enumName })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((newEnum) => {
        SavedEnumsService.pushEnum({
          enumName: enumName,
          enumValue: newEnum.data,
        });
      });

    // save enum
    return this.enumService.enumsGetEnumValues({ EnumTypeName: enumName }).pipe(
      takeUntil(this.onDestroy$),
      map((res) => res.data)
    );
  }
}
